import { Badge, Card, EmptyState, MUTED_TEXT, Spinner, stackStyle } from "@dayvise/react-ui";
import type { LearningEvent } from "@dayvise/client-sdk";
import { ProvenanceGraph, type ProvenanceNode, type ProvenanceEdge } from "./ProvenanceGraph.tsx";
import { useLearningEvents, useLearningRefinements, useMemory } from "./hooks.ts";

/**
 * Provenance view over scoped memory: every edge shown here is a recorded
 * fact (a memory's source conversation, a learning event's source, a
 * refinement's target), never an inferred similarity. Personal memories are
 * grouped separately from shared ones so scope stays visible.
 */
export function MemoryGraphPanel() {
  const memory = useMemory();
  const events = useLearningEvents();
  const refinements = useLearningRefinements();
  const privateMemories = memory.data?.private ?? [];
  const sharedMemories = memory.data?.shared ?? [];
  const eventList = events.data?.events ?? [];
  const refinementList = refinements.data?.refinements ?? [];
  const pending = memory.isPending || events.isPending || refinements.isPending;
  const error = memory.error ?? events.error ?? refinements.error;

  const eventsByConversation = new Map<string, LearningEvent[]>();
  for (const event of eventList) {
    const key = event.sourceConversationId ?? "unlinked";
    const bucket = eventsByConversation.get(key);
    if (bucket) bucket.push(event);
    else eventsByConversation.set(key, [event]);
  }

  const linkedConversations = new Set<string>();
  for (const item of privateMemories) if (item.sourceConversationId) linkedConversations.add(item.sourceConversationId);
  for (const item of sharedMemories) if (item.sourceConversationId) linkedConversations.add(item.sourceConversationId);

  const graphNodes = new Map<string, ProvenanceNode>();
  const graphEdges: ProvenanceEdge[] = [];
  const source = (conversationId: string | null) => {
    if (!conversationId) return null;
    const id = `conversation:${conversationId}`;
    graphNodes.set(id, { id, label: `Conversation ${conversationId.slice(-8)}`, detail: `Recorded source: ${conversationId}`, scope: "source" });
    return id;
  };
  for (const [items, scope] of [[privateMemories, "private"], [sharedMemories, "shared"]] as const) {
    for (const item of items) {
      const id = `memory:${item.memoryId}`;
      graphNodes.set(id, { id, label: item.content.slice(0, 60), detail: item.content, scope });
      const from = source(item.sourceConversationId);
      if (from) graphEdges.push({ from, to: id, label: "Memory recorded from conversation" });
    }
  }
  for (const event of eventList) {
    const id = `learning:${event.eventId}`;
    graphNodes.set(id, { id, label: event.kind, detail: `${event.status}: ${event.summary}`, scope: "private" });
    const from = source(event.sourceConversationId);
    if (from) graphEdges.push({ from, to: id, label: "Learning event recorded from conversation" });
  }
  for (const refinement of refinementList) {
    const id = `refinement:${refinement.refinementId}`;
    graphNodes.set(id, { id, label: `${refinement.state} refinement`, detail: refinement.reason, scope: "private" });
    const from = source(refinement.sourceConversationId);
    if (from) graphEdges.push({ from, to: id, label: "Refinement recorded from conversation" });
    if (refinement.targetId) {
      const target = `${refinement.targetKind}:${refinement.targetId}`;
      if (!graphNodes.has(target)) graphNodes.set(target, { id: target, label: `${refinement.targetKind} target`, detail: refinement.targetId, scope: "private" });
      graphEdges.push({ from: id, to: target, label: "Recorded refinement target" });
    }
  }

  return (
    <Card
      title="Memory provenance"
      subtitle="Each edge is a recorded source: where a memory came from, which conversation a learning event came from, and which memory or skill a refinement changed. Nothing here is inferred."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {pending ? <Spinner label="Loading memory provenance" /> : null}
        {error ? <p role="alert" style={{ color: "var(--dayvise-color-danger)" }}>{error instanceof Error ? error.message : "Could not load provenance"}</p> : null}
        {!pending && privateMemories.length === 0 && sharedMemories.length === 0 && eventList.length === 0 ? (
          <EmptyState title="No recorded provenance yet" description="Memories saved from conversations and recorded learning events appear here with their sources." />
        ) : null}
        {!pending ? <ProvenanceGraph nodes={[...graphNodes.values()]} edges={graphEdges} /> : null}
        {privateMemories.length > 0 ? (
          <div style={stackStyle("var(--dayvise-space-xs)")}>
            <strong>Private memories ({privateMemories.length})</strong>
            {privateMemories.map((item) => (
              <div key={item.memoryId} style={stackStyle("var(--dayvise-space-xs)")}>
                <span>{item.content.length > 140 ? `${item.content.slice(0, 140)}…` : item.content}</span>
                <p style={{ ...MUTED_TEXT, margin: 0 }}>
                  {item.sourceConversationId ? (
                    <>source: conversation {item.sourceConversationId}</>
                  ) : (
                    <>source: recorded directly</>
                  )}
                  {linkedConversations.has(item.sourceConversationId ?? "") && item.sourceConversationId ? null : null}
                </p>
              </div>
            ))}
          </div>
        ) : null}
        {sharedMemories.length > 0 ? (
          <div style={stackStyle("var(--dayvise-space-xs)")}>
            <strong>Shared memories ({sharedMemories.length})</strong>
            {sharedMemories.map((item) => (
              <div key={item.memoryId} style={stackStyle("var(--dayvise-space-xs)")}>
                <span>{item.content.length > 140 ? `${item.content.slice(0, 140)}…` : item.content}</span>
                <p style={{ ...MUTED_TEXT, margin: 0 }}>
                  {item.sourceConversationId ? <>source: conversation {item.sourceConversationId}</> : <>source: recorded directly</>}
                </p>
              </div>
            ))}
          </div>
        ) : null}
        {refinementList.length > 0 ? (
          <div style={stackStyle("var(--dayvise-space-xs)")}>
            <strong>Refinements ({refinementList.length})</strong>
            {refinementList.slice(0, 20).map((refinement) => (
              <div key={refinement.refinementId} style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center", flexWrap: "wrap" }}>
                <Badge tone={refinement.state === "applied" ? "success" : refinement.state === "reverted" ? "warning" : "neutral"}>{refinement.state}</Badge>
                <span>
                  {refinement.targetKind} {refinement.targetId ?? "(workspace)"} · from conversation {refinement.sourceConversationId ?? "unlinked"}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {eventList.length > 0 ? (
          <div style={stackStyle("var(--dayvise-space-xs)")}>
            <strong>Learning events ({eventList.length})</strong>
            {[...eventsByConversation.entries()].map(([conversationId, bucket]) => (
              <div key={conversationId} style={stackStyle("var(--dayvise-space-xs)")}>
                <p style={{ ...MUTED_TEXT, margin: 0 }}>
                  {conversationId === "unlinked" ? "unlinked events" : `conversation ${conversationId}`} · {bucket.length}
                </p>
                {bucket.slice(0, 5).map((event) => (
                  <div key={event.eventId} style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center" }}>
                    <Badge tone="neutral">{event.kind}</Badge>
                    <span>{event.summary.length > 120 ? `${event.summary.slice(0, 120)}…` : event.summary}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
